import ContextMenu, { ContextMenuItem } from '@/components/common/ContextMenu';
import Divider from '@/components/common/Divider';
import LiteOrderModal from '@/components/modal/LiteOrderModal';
import { qtyFormatterConfig } from '@/config/config';
import { roundedLgClassname, shouldUseNopBalance } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import {
    BalanceItem,
    CelerBalance,
    CollapsedBalances,
    selectAggregatedBalance,
    selectBalanceStatus,
    selectNopBalance
} from '@/state/reducers/balanceSlice';
import { isSameUTCDay } from '@/utils/format';
import useContextMenu from '@/utils/hooks/useContextMenu';
import { getCurrency } from '@/utils/hooks/useCurrency';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import useLanguage from '@/utils/hooks/useLanguage';
import cn from 'classnames';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import { MdAddCircle, MdAutoAwesomeMotion, MdRemoveCircle } from 'react-icons/md';
import LiteActions from '../LiteActions';
import LiteBalance from './LiteBalance';
import { OldLiteBalance } from './OldLiteBalance';

interface LiteBalancesProps {}

export default function LiteBalances({ currencyConfigDict }: any) {
    const status = useAppSelector(selectBalanceStatus);
    const nopBalance = useAppSelector(selectNopBalance);
    const celerBalance = useAppSelector(selectAggregatedBalance);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const lang = useLanguage();
    const formatQty = useNumberFormatter(qtyFormatterConfig);

    const [opened, handlers] = useDisclosure(false);

    const [selectedBalance, setSelectedBalance] = useState<CollapsedBalances>();
    const [selectedCelerBalance, setSelectedCelerBalance] = useState<CelerBalance>();
    const [showBalanceSettlements, setShowBalanceSettlements] = useState<Record<string, boolean>>({});

    const [initFrom, setInitFrom] = useState<string>('');
    const [initTo, setInitTo] = useState<string>('');

    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        useContextMenu();

    const onSelectingBalance = useCallback(
        (e: any, balance: CollapsedBalances) => {
            setSelectedBalance(balance);
            handleOpenContextMenu(e);
        },
        [handleOpenContextMenu]
    );

    const onSelectingCelerBalance = useCallback(
        (e: any, balance: CelerBalance) => {
            setSelectedCelerBalance(balance);
            handleOpenContextMenu(e);
        },
        [handleOpenContextMenu]
    );

    const showSettlements = useMemo(() => {
        if (selectedBalance?.rows) {
            const hasSettlements = selectedBalance.rows.length > 0;
            const allSettlementsAreToday = selectedBalance.rows.every((row) => isSameUTCDay(row.valueDate));
            return hasSettlements && !allSettlementsAreToday;
        } else {
            return false;
        }
    }, [selectedBalance?.rows]);

    const balances = useMemo(() => {
        return shouldUseNopBalance ? nopBalance?.balances || [] : celerBalance;
    }, [nopBalance, celerBalance]);

    const isSameSettlementDate = useMemo(() => {
        const flattenDates = balances.flatMap((balance) => balance.rows).map((row) => row.valueDate);
        const set = Array.from(new Set(flattenDates));
        return set.length === 1;
    }, [balances]);

    const nopBalanceAccountMatched = useMemo(() => {
        return currentAccount === nopBalance?.account;
    }, [currentAccount, nopBalance]);

    const selectedBalanceCurrency = useMemo(() => selectedBalance?.currency || '', [selectedBalance]);

    const cryptoBalances = useMemo(() => {
        return balances.filter((balance) => getCurrency(balance.currency)?.type === 'Crypto');
    }, [balances]);

    const fiatBalances = useMemo(() => {
        return balances.filter((balance) => getCurrency(balance.currency)?.type === 'Fiat');
    }, [balances]);

    const balanceTotals = useMemo(() => {
        return shouldUseNopBalance
            ? balances.reduce((acc, balance: any) => acc + balance.totalAmountInBaseCurrency, 0) || 0
            : balances.reduce((acc, balance: any) => acc + balance.netPosition, 0) || 0;
    }, [balances]);

    const shouldShowNopBalance = useMemo(() => {
        return shouldUseNopBalance ? nopBalanceAccountMatched : true;
    }, []);

    return (
        <Fragment>
            <div
                className={cn(
                    'flex flex-col h-fit w-full gap-2 sm:gap-3 sm:bg-brand-background-dark p-0 sm:p-6',
                    roundedLgClassname
                )}>
                <span className="text-brand-text font-bold text-base sm:text-lg px-2 sm:p-0">
                    {lang.lite.yourBalances}
                </span>
                <LiteActions orderModal={handlers} setInitFrom={setInitFrom} setInitTo={setInitTo} />
                <div className="flex flex-col gap-2 sm:gap-3">
                    {status === 'OPEN' && shouldShowNopBalance && (
                        <Fragment>
                            {cryptoBalances.length > 0 && (
                                <div className="flex flex-col items-center gap-2 sm:gap-3">
                                    <div className="flex w-full justify-between">
                                        <span className="text-sm sm:text-base px-2 sm:p-0">
                                            {lang.lite.cryptoAssets}
                                        </span>
                                    </div>
                                    <div className="flex w-full text-xs sm:text-sm text-brand-neutral-400 gap-2 sm:gap-3 px-2 sm:px-3">
                                        <span className="flex-[1_1_0]">{lang.lite.asset}</span>
                                        <span className="flex-[1_1_0] text-end">{lang.balances.balance}</span>
                                        {nopBalance && shouldUseNopBalance && (
                                            <div className="text-right flex-[1_1_0]">
                                                {lang.balances.balance}{' '}
                                                {nopBalance.baseCurrency ? `(${nopBalance.baseCurrency})` : ''}
                                            </div>
                                        )}
                                    </div>
                                    <Divider className="bg-brand-neutral-700" />
                                    {cryptoBalances.map((balance) => (
                                        <Fragment key={balance.currency}>
                                            {shouldUseNopBalance ? (
                                                <LiteBalance
                                                    balance={balance as CollapsedBalances}
                                                    settlementsDisclosure={showBalanceSettlements[balance.currency]}
                                                    onClick={onSelectingBalance}
                                                />
                                            ) : (
                                                <OldLiteBalance
                                                    balance={balance as BalanceItem}
                                                    onClick={onSelectingCelerBalance}
                                                />
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                            )}
                            {fiatBalances.length > 0 && (
                                <div className="flex flex-col items-center gap-2 sm:gap-3">
                                    <div className="flex w-full justify-between">
                                        <span className="text-sm sm:text-base px-2 sm:p-0">
                                            {lang.lite.fiatCurrencies}
                                        </span>
                                    </div>
                                    <div className="flex w-full text-xs sm:text-sm text-brand-neutral-400 gap-2 sm:gap-3 px-2 sm:px-3">
                                        <span className="flex-[1_1_0]">{lang.lite.currency}</span>
                                        <span className="flex-[1_1_0] text-end">{lang.balances.balance}</span>
                                        {nopBalance && shouldUseNopBalance && (
                                            <div className="text-right flex-[1_1_0]">
                                                {lang.balances.balance}{' '}
                                                {nopBalance.baseCurrency ? `(${nopBalance.baseCurrency})` : ''}
                                            </div>
                                        )}
                                    </div>
                                    <Divider className="bg-brand-neutral-700" />
                                    {fiatBalances.map((balance) => (
                                        <Fragment key={balance.currency}>
                                            {shouldUseNopBalance ? (
                                                <LiteBalance
                                                    balance={balance as CollapsedBalances}
                                                    settlementsDisclosure={showBalanceSettlements[balance.currency]}
                                                    onClick={onSelectingBalance}
                                                />
                                            ) : (
                                                <OldLiteBalance
                                                    balance={balance as BalanceItem}
                                                    onClick={onSelectingCelerBalance}
                                                />
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                            )}
                            {balanceTotals !== undefined && (
                                <div className="flex font-semibold pt-2 px-2 sm:p-0">
                                    <div className="text-left flex-[1_1_0]">{lang.balances.total}</div>
                                    <div
                                        className={cn(
                                            'flex flex-[2_1_0] relative items-center justify-center gap-2 text-right',
                                            {
                                                ['text-brand-text']: balanceTotals === 0,
                                                ['text-brand-green']: balanceTotals > 0,
                                                ['text-brand-red']: balanceTotals < 0
                                            }
                                        )}>
                                        <span className="flex-[1_1_0]">{formatQty.format(balanceTotals)}</span>
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    )}
                </div>
                {status === 'CLOSED' && (
                    <div className="flex flex-col gap-2 p-2 justify-center items-center h-full text-xs sm:text-sm text-center text-brand-neutral-400">
                        <div className="italic">{lang.balances.unableToFetch}</div>
                        <div className="italic">{lang.balances.pleaseReachOut}</div>
                    </div>
                )}
            </div>

            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}>
                {!isSameSettlementDate && showSettlements && shouldShowNopBalance && (
                    <ContextMenuItem
                        onClick={() => {
                            const newShowBalanceSettlements = { ...showBalanceSettlements };
                            newShowBalanceSettlements[selectedBalanceCurrency] =
                                !showBalanceSettlements[selectedBalanceCurrency];
                            setShowBalanceSettlements(newShowBalanceSettlements);
                        }}>
                        <MdAutoAwesomeMotion />
                        <span>
                            {!showBalanceSettlements[selectedBalanceCurrency] ? 'Show' : 'Hide'} {lang.lite.settlements}
                        </span>
                    </ContextMenuItem>
                )}
                <ContextMenuItem
                    onClick={() => {
                        setInitFrom('');
                        setInitTo(selectedBalanceCurrency);
                        handlers.toggle();
                    }}>
                    <MdAddCircle />
                    <span>
                        {lang.orderForm.buy} {selectedBalanceCurrency}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem
                    className="hover:bg-brand-red"
                    mode="secondary"
                    onClick={() => {
                        setInitFrom(selectedBalanceCurrency);
                        setInitTo('');
                        handlers.toggle();
                    }}>
                    <MdRemoveCircle />
                    <span>
                        {lang.orderForm.sell} {selectedBalanceCurrency}
                    </span>
                </ContextMenuItem>
            </ContextMenu>
            <LiteOrderModal initFrom={initFrom} initTo={initTo} opened={opened} handlers={handlers} />
        </Fragment>
    );
}
